import { useState, useMemo } from 'react'

export interface SortState {
  col: string
  dir: 'asc' | 'desc'
}

/**
 * useSortable — client-side sort for any typed row array.
 *
 * Usage:
 *   const { sort, onSort, sorted } = useSortable(rows, 'shipment_date', 'desc')
 *
 * Then pass `sort` and `onSort` to <SortableHeader> and render `sorted` in the tbody.
 *
 * Numeric strings (e.g. amounts returned as strings from the API) are coerced
 * to Number before comparison so "$35.24" sorts correctly.
 */
export function useSortable<T extends object>(
  rows: T[],
  defaultCol: string,
  defaultDir: 'asc' | 'desc' = 'asc',
) {
  const [sort, setSort] = useState<SortState>({ col: defaultCol, dir: defaultDir })

  function onSort(col: string) {
    setSort(prev =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'asc' },
    )
  }

  const sorted = useMemo(() => {
    if (!rows.length) return rows
    return [...rows].sort((a, b) => {
      const av = (a as Record<string, unknown>)[sort.col]
      const bv = (b as Record<string, unknown>)[sort.col]
      // nulls last
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      // numeric coercion (covers string amounts like "35.24")
      const an = Number(av)
      const bn = Number(bv)
      const cmp =
        !isNaN(an) && !isNaN(bn)
          ? an - bn
          : String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' })
      return sort.dir === 'asc' ? cmp : -cmp
    })
  }, [rows, sort.col, sort.dir])

  return { sort, onSort, sorted }
}
